import { useEffect, useState } from 'react'

import { transcriptionApiKey } from '#/shared/lib/stt'

/**
 * Which dictation engine a device gets, and whether it has one at all.
 *
 * Desktop streams the mic to ElevenLabs Scribe (the same engine as the live
 * call transcript), which needs the transcription API key. Phones/tablets use
 * the browser's built-in `SpeechRecognition` instead — native mic handling,
 * no streaming pipeline to keep alive on a mobile tab.
 */

/** What every dictation hook returns, so the per-device engines are
 *  interchangeable behind `useVoiceDictation`. */
export interface DictationSession {
  recording: boolean
  /** True while the engine is getting ready (mic grant / stream opening). */
  loading: boolean
  /** True from stop until the final transcript has been emitted. */
  processing: boolean
  /** Live mic stream for the waveform — null on the native engine, which
   *  owns its microphone internally. */
  stream: MediaStream | null
  start: () => void | Promise<void>
  stop: () => void
}

export interface SpeechRecognitionAlternativeLike {
  readonly transcript: string
}

export interface SpeechRecognitionResultLike {
  readonly isFinal: boolean
  readonly 0: SpeechRecognitionAlternativeLike
}

export interface SpeechRecognitionResultListLike {
  readonly length: number
  readonly [index: number]: SpeechRecognitionResultLike
}

export interface SpeechRecognitionEventLike {
  readonly resultIndex: number
  readonly results: SpeechRecognitionResultListLike
}

export interface SpeechRecognitionErrorEventLike {
  readonly error: string
}

/** The slice of the Web Speech API we drive — typed locally because
 *  `SpeechRecognition` isn't in TypeScript's DOM lib. */
export interface SpeechRecognitionLike {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  onstart: (() => void) | null
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike

/** The browser's SpeechRecognition constructor, or null where the Web Speech
 *  API isn't available (notably Firefox for Android). */
export function getSpeechRecognition(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

/** Phone or tablet — the devices that dictate through the native speech API
 *  instead of the streaming engine (see module doc). */
export function usesNativeDictation(): boolean {
  if (typeof navigator === 'undefined') return false
  if (/android|iphone|ipad|ipod/i.test(navigator.userAgent)) return true
  // iPadOS 13+ reports itself as desktop macOS; multi-touch gives it away.
  return /mac/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1
}

/** Whether this device has a working dictation path at all: desktop needs the
 *  transcription API key, mobile needs the native speech API. */
export function isDictationSupported(): boolean {
  return usesNativeDictation()
    ? getSpeechRecognition() !== null
    : transcriptionApiKey() !== undefined
}

/**
 * SSR-safe `isDictationSupported()` for hiding mic UI. Renders as supported on
 * the server and first client paint (so hydration matches), then settles to
 * the real answer — only the rare unsupported browser sees the mic disappear.
 */
export function useDictationSupported(): boolean {
  const [supported, setSupported] = useState(true)
  useEffect(() => {
    setSupported(isDictationSupported())
  }, [])
  return supported
}
